// ─────────────────────────────────────────────────────────────────────────
// Creative Strategist — the brand template approval layer's shared vocabulary
// (Task #2380, spec §29–§37).
//
// THE single place the asset-type catalog lives. Client pages, server routes,
// the discovery read path and the tests all import from here — the list is
// never re-declared in a component or a route handler.
//
// Pure module: no DB, no server imports, so the critical-test gate can load it.
//
// What this layer is: a per-brand judgement over templates the underlying
// PersonaOS systems ALREADY let the brand use. It only ever narrows. Nothing
// here grants access to an ad unit or a landing-page type the brand is not
// already permitted to use.
// ─────────────────────────────────────────────────────────────────────────

// ── the systems that actually own templates ────────────────────────────────
//
// `underlying_system` on a registry row. A registry row is a REFERENCE to a
// row in one of these systems — their content is never copied.
export const CREATIVE_STRATEGIST_TEMPLATE_SYSTEMS = [
  "ad_unit_types",
  "landing_page_template_types",
  "email_express",
] as const;
export type CreativeStrategistTemplateSystem =
  (typeof CREATIVE_STRATEGIST_TEMPLATE_SYSTEMS)[number];

export const CREATIVE_STRATEGIST_TEMPLATE_SYSTEM_LABELS: Record<
  CreativeStrategistTemplateSystem,
  string
> = {
  ad_unit_types: "Ad Units",
  landing_page_template_types: "LP Express",
  email_express: "Email Express",
};

// Where a registry row came from.
export const CREATIVE_STRATEGIST_TEMPLATE_SOURCES = [
  // Discovered from the underlying system and reviewed by a human.
  "discovered",
  // Added by a user by hand.
  "user",
  // Proposed by Creative Strategist itself — always starts as TESTING.
  "strategist",
  // Copied from another registry row.
  "duplicate",
] as const;
export type CreativeStrategistTemplateSource =
  (typeof CREATIVE_STRATEGIST_TEMPLATE_SOURCES)[number];

// ── asset types (spec §30) ─────────────────────────────────────────────────
//
// Extensible on purpose: add an entry here (and, for a system with a
// discoverable catalog, a classification rule below) and the coverage table,
// the gallery, the admin view and selection all pick it up. Nothing downstream
// hardcodes the twelve.

export interface CreativeStrategistAssetTypeDef {
  key: string;
  label: string;
  /** One line explaining what this asset type produces. */
  description: string;
  /** The PersonaOS system whose templates back this asset type. */
  system: CreativeStrategistTemplateSystem;
  sortOrder: number;
  /**
   * False when the underlying system has no per-brand catalog we can read yet
   * — the gallery then shows registry rows only (nothing is auto-discovered),
   * and coverage still reports honestly.
   */
  discoverable: boolean;
}

export const CREATIVE_STRATEGIST_ASSET_TYPES: CreativeStrategistAssetTypeDef[] = [
  {
    key: "static_ad",
    label: "Static Ads",
    description: "Single-frame paid social and display ads.",
    system: "ad_unit_types",
    sortOrder: 10,
    discoverable: true,
  },
  {
    key: "landing_page",
    label: "Landing Pages",
    description: "Standalone conversion pages built in LP Express.",
    system: "landing_page_template_types",
    sortOrder: 20,
    discoverable: true,
  },
  {
    key: "advertorial",
    label: "Advertorials",
    description: "Editorial-style article pages that sell.",
    system: "landing_page_template_types",
    sortOrder: 30,
    discoverable: true,
  },
  {
    key: "listicle",
    label: "Listicles",
    description: "Numbered-reason buyer's-guide pages.",
    system: "landing_page_template_types",
    sortOrder: 40,
    discoverable: true,
  },
  {
    key: "quiz_funnel",
    label: "Quiz Funnels",
    description: "Scored quiz funnels that route to a recommendation.",
    system: "landing_page_template_types",
    sortOrder: 50,
    discoverable: true,
  },
  {
    key: "offer_page",
    label: "Offer Pages",
    description: "Value-stack and offer-led close pages.",
    system: "landing_page_template_types",
    sortOrder: 60,
    discoverable: true,
  },
  {
    key: "ugc",
    label: "UGC",
    description: "Creator-style testimonial and hand-held video.",
    system: "ad_unit_types",
    sortOrder: 70,
    discoverable: true,
  },
  {
    key: "product_demo_video",
    label: "Product Demo Video",
    description: "Product-in-use demonstrations and unboxings.",
    system: "ad_unit_types",
    sortOrder: 80,
    discoverable: true,
  },
  {
    key: "founder_video",
    label: "Founder Video",
    description: "Founder-to-camera brand and origin stories.",
    system: "ad_unit_types",
    sortOrder: 90,
    discoverable: true,
  },
  {
    key: "email",
    label: "Email",
    description: "Campaign and flow emails built in Email Express.",
    system: "email_express",
    // Email Express has no per-brand template catalog to read yet, so nothing
    // is auto-discovered — approved rows are added deliberately.
    sortOrder: 100,
    discoverable: false,
  },
  {
    key: "other_video",
    label: "Other Video",
    description: "Video formats that are not UGC, demo or founder-led.",
    system: "ad_unit_types",
    sortOrder: 110,
    discoverable: true,
  },
  {
    key: "carousel",
    label: "Carousels / Multi-Frame",
    description: "Multi-frame carousels and swipe sequences.",
    system: "ad_unit_types",
    sortOrder: 120,
    discoverable: true,
  },
];

const ASSET_TYPE_BY_KEY = new Map(
  CREATIVE_STRATEGIST_ASSET_TYPES.map((t) => [t.key, t]),
);

export function assetTypeDef(
  key: string | null | undefined,
): CreativeStrategistAssetTypeDef | null {
  return (key && ASSET_TYPE_BY_KEY.get(key)) || null;
}

export function isCreativeStrategistAssetType(key: unknown): key is string {
  return typeof key === "string" && ASSET_TYPE_BY_KEY.has(key);
}

export function assetTypeLabel(key: string | null | undefined): string {
  return assetTypeDef(key)?.label ?? (key || "Unknown");
}

export function assetTypesForSystem(
  system: CreativeStrategistTemplateSystem,
): CreativeStrategistAssetTypeDef[] {
  return CREATIVE_STRATEGIST_ASSET_TYPES.filter((t) => t.system === system);
}

// ── statuses (spec §33) ────────────────────────────────────────────────────
//
// The status list itself lives in shared/schema.ts (it is a column contract);
// presentation and behaviour live here.

/** Statuses Creative Strategist may pick from automatically. */
export const CS_SELECTABLE_TEMPLATE_STATUSES = ["DEFAULT", "APPROVED"] as const;

export const CS_TEMPLATE_STATUS_LABELS: Record<string, string> = {
  UNREVIEWED: "Unreviewed",
  TESTING: "Testing",
  APPROVED: "Approved",
  DEFAULT: "Default",
  RETIRED: "Retired",
};

export const CS_TEMPLATE_STATUS_HELP: Record<string, string> = {
  UNREVIEWED: "Exists, but Creative Strategist cannot use it automatically.",
  TESTING: "Can be selected deliberately as an experiment.",
  APPROVED: "Creative Strategist may use it.",
  DEFAULT: "The preferred option for this asset type.",
  RETIRED: "Never used for new work.",
};

export const CS_TEMPLATE_STATUS_CLASSES: Record<string, string> = {
  UNREVIEWED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  TESTING: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  DEFAULT: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  RETIRED: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

/** Coverage readiness for one asset type (spec §31). */
export type CreativeStrategistCoverageState =
  | "ready"
  | "needs_default"
  | "needs_approval";

export const CS_COVERAGE_LABELS: Record<CreativeStrategistCoverageState, string> = {
  ready: "Ready",
  needs_default: "Needs Default",
  needs_approval: "Needs Approval",
};

export function coverageState(counts: {
  hasDefault: boolean;
  approvedCount: number;
}): CreativeStrategistCoverageState {
  if (counts.hasDefault) return "ready";
  if (counts.approvedCount > 0) return "needs_default";
  return "needs_approval";
}

// ── classification: underlying template → asset type ───────────────────────
//
// Discovery lists what a brand may already use; these rules decide which
// gallery a candidate shows up in. They affect PRESENTATION only — a
// misfiled candidate is still un-usable until a human approves it, and
// approving never widens access.

/**
 * Landing-page template-type key → asset type. Keys not listed here fall back
 * to "landing_page", so a new LP template added to the catalog shows up
 * immediately instead of disappearing.
 */
export const LP_TEMPLATE_KEY_ASSET_TYPES: Record<string, string> = {
  advertorial: "advertorial",
  listicle: "listicle",
  quiz: "quiz_funnel",
  "offer-stack": "offer_page",
  "cold-drip": "offer_page",
};

export function assetTypeForLpTemplateKey(key: string | null | undefined): string {
  if (!key) return "landing_page";
  return LP_TEMPLATE_KEY_ASSET_TYPES[key] ?? "landing_page";
}

export interface AdUnitClassificationInput {
  name?: string | null;
  slug?: string | null;
  formatType?: string | null;
}

/**
 * Ad unit → asset type. Name/slug wording wins over `format_type` because the
 * format column is coarse (and historically inconsistent); anything that does
 * not read as video or multi-frame is a static ad.
 */
export function assetTypeForAdUnit(unit: AdUnitClassificationInput): string {
  const text = `${unit.name ?? ""} ${unit.slug ?? ""}`.toLowerCase();
  const format = (unit.formatType ?? "").toLowerCase();

  if (format === "carousel" || /\bcarousel\b|multi[-\s]?frame|swipe/.test(text)) {
    return "carousel";
  }
  if (/\bfounder\b|\bceo\b/.test(text)) return "founder_video";
  if (/\bdemo\b|demonstration|unboxing|how[-\s]?to[-\s]?use/.test(text)) {
    return "product_demo_video";
  }
  if (format === "ugc" || /\bugc\b|creator|testimonial video/.test(text)) return "ugc";
  if (format === "video" || format === "pixar" || /\bvideo\b|\bvsl\b/.test(text)) {
    return "other_video";
  }
  return "static_ad";
}

// ── the missing-template blocker (spec §36) ────────────────────────────────

export const CS_BLOCKER_TEMPLATE_APPROVAL = "template_approval_required";

export const CS_BLOCKER_STATUSES = ["open", "resolved"] as const;
export type CreativeStrategistBlockerStatus = (typeof CS_BLOCKER_STATUSES)[number];

export interface CreativeStrategistBlockerOption {
  key: string;
  label: string;
  description: string;
}

/** The four options the spec requires on a Template Approval Required blocker. */
export const CS_TEMPLATE_BLOCKER_OPTIONS: CreativeStrategistBlockerOption[] = [
  {
    key: "use_fallback",
    label: "Use approved general fallback",
    description:
      "Produce the asset with a template this brand has already approved for a related asset type.",
  },
  {
    key: "propose_template",
    label: "Propose new template",
    description:
      "Let Creative Strategist put a candidate forward. It starts as Testing and waits for your approval.",
  },
  {
    key: "ask_user",
    label: "Ask user",
    description: "Pause here and come back once you have picked a template yourself.",
  },
  {
    key: "skip_asset",
    label: "Skip asset",
    description: "Drop this asset type from the project and carry on with the rest.",
  },
];

export const CS_TEMPLATE_BLOCKER_OPTION_KEYS = CS_TEMPLATE_BLOCKER_OPTIONS.map(
  (o) => o.key,
);

export function isTemplateBlockerOption(key: unknown): key is string {
  return typeof key === "string" && CS_TEMPLATE_BLOCKER_OPTION_KEYS.includes(key);
}

// ── rule lists (spec §35) ──────────────────────────────────────────────────

export const CS_TEMPLATE_RULE_FIELDS = [
  "approvedFor",
  "avoidFor",
  "lockedRules",
  "flexibleRules",
] as const;
export type CreativeStrategistTemplateRuleField =
  (typeof CS_TEMPLATE_RULE_FIELDS)[number];

export const CS_TEMPLATE_RULE_LABELS: Record<
  CreativeStrategistTemplateRuleField,
  string
> = {
  approvedFor: "Best For",
  avoidFor: "Avoid For",
  lockedRules: "Locked Elements",
  flexibleRules: "Flexible Elements",
};

export const CS_TEMPLATE_RULE_PLACEHOLDERS: Record<
  CreativeStrategistTemplateRuleField,
  string
> = {
  approvedFor: "e.g. Pain recognition",
  avoidFor: "e.g. Long educational copy",
  lockedRules: "e.g. Logo placement",
  flexibleRules: "e.g. Headline",
};

/** Normalize a jsonb rule list into a clean string array. */
export function ruleList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== "string") continue;
    const s = item.trim().slice(0, 200);
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out.slice(0, 50);
}
